import { ApprovalMode } from "../approvalModes";
import {
  type ClientToolDeps,
  type ClientToolExecutionResult,
  createClientTool,
  toolError,
} from "../ClientTools";

import { RemovePanelConfirmation } from "./confirmations";
import { runPanelMutation, visualizerStatus } from "./workspaceActions";

// ---------------------------------------------------------------------------
// Input types
// ---------------------------------------------------------------------------

export interface RemovePanelInput {
  panel_id: string;
}

// ---------------------------------------------------------------------------
// Execute
// ---------------------------------------------------------------------------

function execute(
  input: RemovePanelInput,
  deps?: ClientToolDeps,
): ClientToolExecutionResult {
  if (!input.panel_id) {
    return toolError("panel_id is required");
  }

  // Not `isOnVisualizerPage()`: inside a batch that closure answers the
  // pre-batch route, so it still reads true for a page a batch-mate is
  // replacing. Publishing into that workspace would report success against
  // panels that are about to be torn down.
  if (!deps?.publishWorkspaceAction || visualizerStatus(deps) !== "open") {
    return toolError(
      "The visualizer is not open, so there are no panels to modify. " +
        "Use client_visualize_open_panel to open data first.",
    );
  }

  return runPanelMutation(
    deps.publishWorkspaceAction,
    "remove_panel",
    (onResult) => ({
      type: "remove_panel",
      payload: {
        panelId: input.panel_id,
        onResult,
      },
    }),
  );
}

// ---------------------------------------------------------------------------
// Tool module export
// ---------------------------------------------------------------------------

export const tool = createClientTool<RemovePanelInput>({
  name: "client_visualize_remove_panel",
  description: `Close an entire visualizer panel, removing it from the workspace layout. The panel is referenced by its id from the visualizer state context. Only works while the user is on the visualizer page. The underlying data is not deleted — the same message paths can be opened in a new panel with client_visualize_open_panel.`,
  input_schema: {
    type: "object",
    properties: {
      panel_id: {
        type: "string",
        description:
          "Id of the panel to close. Panel ids are visible in the visualizer state context.",
      },
    },
    required: ["panel_id"],
    additionalProperties: false,
  },
  execute,
  defaultApprovalMode: ApprovalMode.AUTO_EXECUTE,
  allowableApprovalModes: [
    ApprovalMode.REQUIRE_APPROVAL,
    ApprovalMode.FOLLOW_AUTO,
    ApprovalMode.AUTO_EXECUTE,
  ],
  displayName: "Remove Panel",
  displayDescription: "Closes a panel in the visualizer.",
  ConfirmationContent: RemovePanelConfirmation,
});
